import React, { useState, useRef, useEffect } from 'react';
import { PencilLine, Check, X, Loader2 } from 'lucide-react';

export default function InlineDescriptionCell({ value, onSave, rowId }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleSave = async () => {
    if (draft === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(rowId, draft);
      setEditing(false);
    } catch {
      // Error handled by parent
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(value);
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (editing) {
    return (
      <div className="flex items-start gap-1 w-full min-w-[300px]">
        <textarea
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-1 min-h-[60px] text-sm p-2 border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-ring resize-y font-[inherit]"
          data-testid="inline-description-input"
          disabled={saving}
        />
        <div className="flex flex-col gap-1 pt-1">
          <button
            onClick={handleSave}
            disabled={saving}
            className="p-1 rounded hover:bg-primary/10 text-primary transition-colors duration-150"
            data-testid="inline-description-save-button"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          </button>
          <button
            onClick={handleCancel}
            disabled={saving}
            className="p-1 rounded hover:bg-destructive/10 text-muted-foreground transition-colors duration-150"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="group flex items-start gap-2 cursor-pointer min-w-[300px] py-1 px-2 -mx-2 rounded hover:bg-muted/50 transition-colors duration-150"
      onClick={() => setEditing(true)}
      data-testid="inline-description-cell"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && setEditing(true)}
    >
      <span className="text-sm leading-relaxed flex-1 break-words">{value || <em className="text-muted-foreground">No description</em>}</span>
      <PencilLine className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-150 mt-0.5 flex-shrink-0" />
    </div>
  );
}
